import { CircularProgress, withStyles } from "@material-ui/core";
import Modal from "@material-ui/core/Modal";
import PropTypes from "prop-types";
import React from "react";
import areObjectsDifferent from "../../utils/areObjectsDifferent";
import { getAvailableServices } from "../../utils/get-available-services";
import { breakpoints } from "../../styles/breakpoints";
import QuoteStepperHorizontal from "./quote.stepper.component";

const styles = (theme) => ({
  paper: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    [`@media (max-width: ${breakpoints.md}px)`]: {
      width: "100vw",
      height: "100dvh",
    },
    width: "96vw",
    maxWidth: 1200,
    height: "92dvh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    backgroundImage: `linear-gradient(
      rgba(255, 255, 255, 0.93),
      rgba(248, 250, 252, 0.88)
    ), url(/assets/images/city.svg)`,
    backgroundRepeat: "no-repeat, no-repeat",
    backgroundSize: "100% 100%, 100% 30%",
    backgroundPosition: "center, center bottom",
    overflow: "hidden",
  },
  /**
   * Footer CTAs only below `lg`. Layout lives in JSS (not inline) so `display: none` at lg+ is not
   * overridden by inline styles — inline `display: flex` previously kept the bar visible on desktop.
   */
  modalFooter: {
    flex: "0 0 auto",
    width: "100%",
    minHeight: 55,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Open Sans', sans-serif",
    fontSize: 21,
    fontWeight: "bold",
    color: "white",
    [`@media (min-width: ${breakpoints.lg}px)`]: {
      display: "none",
    },
  },
});

class SimpleModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      step: 0,
      open: false,
      payButtonEnbabled: false,
      cardInfo: {},
      startPayment: false,
    };

    this.isFetchingServices = false;
    this.fetchServicesDebounceTimer = null;

    this.company = props.company;

    this.setStep = this.setStep.bind(this);
    this.togglePayButton = this.togglePayButton.bind(this);
    this.submitCardInfo = this.submitCardInfo.bind(this);
    this.saveCardInfo = this.saveCardInfo.bind(this);
  }

  chargeIt = null;

  handleOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  payStep = (e) => {
    this.setState({ step: 1 });
  };

  setStep = (val) => {
    this.setState({ step: val });
  };

  submitCardInfo = () => {
    const { payButtonEnbabled, cardInfo } = this.state;
    const price =
      this.props.term === "total" ? this.props.allTotal : this.props.firstTotal;

    if (
      (!payButtonEnbabled && !this.props.bypassPayment) ||
      (this.props.bypassPayment && price === 0)
    )
      return false;

    if (this.props.cardProcessor === "lawnbot-credit-card-vault") {
      this.chargeIt();
    } else {
      this.props.submitPayment("card", cardInfo);
    }
  };

  saveCardInfo = (cardInfo) => {
    this.setState({ cardInfo });
  };

  togglePayButton = (buttonState) => {
    if (buttonState === "enable") {
      this.setState({ payButtonEnbabled: true });
    } else {
      this.setState({ payButtonEnbabled: false });
    }
  };

  chargePciProxy = (fn) => {
    this.chargeIt = fn;
  };

  componentWillUnmount() {
    if (this.fetchServicesDebounceTimer) {
      clearTimeout(this.fetchServicesDebounceTimer);
    }
  }

  async componentDidUpdate(prevProps) {
    if (!this.props.quoteExists) return;

    const normalizeValue = (value) => (Number.isNaN(value) ? 0 : value);
    const prevLotSize = normalizeValue(prevProps.adjustedLotSize);
    const currentLotSize = normalizeValue(this.props.adjustedLotSize);

    const hasChanged =
      prevProps.selectedServices !== this.props.selectedServices ||
      prevProps.company !== this.props.company ||
      prevProps.botId !== this.props.botId ||
      prevProps.quoteId !== this.props.quoteId ||
      prevLotSize !== currentLotSize ||
      prevProps.customerZip !== this.props.customerZip ||
      areObjectsDifferent(prevProps.answers, this.props.answers);

    const skipMeasurement = this.props.skipMeasurement === true;
    const estimateIsValid =
      skipMeasurement ||
      (this.props.estimate &&
        !Number.isNaN(this.props.estimate) &&
        this.props.estimate >= this.props.minQuotableSize &&
        this.props.estimate <= this.props.maxQuotableSize);

    if (hasChanged && estimateIsValid) {
      if (this.fetchServicesDebounceTimer) {
        clearTimeout(this.fetchServicesDebounceTimer);
      }

      this.fetchServicesDebounceTimer = setTimeout(async () => {
        if (this.isFetchingServices) return;

        this.isFetchingServices = true;
        try {
          const services = await getAvailableServices({
            serviceTypes: this.props.selectedServices,
            companyId: this.props.company,
            botId: this.props.botId,
            quoteId: this.props.quoteId,
            estimate: this.props.adjustedLotSize,
            skipMeasurement,
            answers: this.props.answers,
            onboarding: this.onboarding,
            preview: this.preview,
            street: this.props.customerAddress,
            city: this.props.customerCity,
            postalCode: this.props.customerZip,
            county: this.props.customerCounty,
            state: this.props.customerState,
            country:
              this.props.customerCountryCode || this.props.customerCountry,
          });
          this.props.addToAvailableServices(services);
        } finally {
          this.isFetchingServices = false;
        }
      }, 300);
    }
  }

  getButtonLabel = () => {
    if (this.props.bypassPayment) return "⚡ BUY NOW";
    if (this.state.step === 0) return "Sign Up";
    return this.props.term === "application" ? "Checkout" : "Sign Up";
  };

  getButtonAction = () => {
    if (this.props.bypassPayment) return this.submitCardInfo;
    return this.state.step === 0 ? this.payStep : this.submitCardInfo;
  };

  getButtonEnabled = () => {
    const price =
      this.props.term === "total" ? this.props.allTotal : this.props.firstTotal;
    const taxAmount = price * (this.props.serviceTax / 100);
    const totalPrice = price + taxAmount;

    if (this.props.bypassPayment) {
      return totalPrice > 0 && !this.props.charging;
    }
    if (this.state.step === 0) return true;
    return this.state.payButtonEnbabled && !this.props.charging;
  };

  render() {
    const { classes } = this.props;

    return (
      <div>
        <Modal
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          open={this.state.open}
          onClose={this.handleOpen}
        >
          <div className={classes.paper}>
            <div style={{ flex: "1 1 0%", minHeight: 0, overflow: "hidden" }}>
              <QuoteStepperHorizontal
                {...this.props}
                registerCharge={this.chargePciProxy}
                currentStep={this.state.step}
                setStep={this.setStep}
                togglePayButton={this.togglePayButton}
                saveCardInfo={this.saveCardInfo}
              />
            </div>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
            <div
              className={classes.modalFooter}
              onClick={this.getButtonAction()}
              style={{
                backgroundColor: this.getButtonEnabled() ? "#1565C0" : "#ccc",
                cursor: this.getButtonEnabled() ? "pointer" : "default",
              }}
            >
              {this.props.charging ? (
                <CircularProgress style={{ color: "white" }} />
              ) : (
                <span>{this.getButtonLabel()}</span>
              )}
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

SimpleModal.propTypes = {
  submitPayment: PropTypes.func,
  skipMeasurement: PropTypes.bool,
};
export default withStyles(styles)(SimpleModal);
